import React, { useEffect, useMemo, useState } from "react";
import { Client } from "../../interfaces/client";
import { AdditionalContact } from "../../interfaces/additionalContact";
import { DeliveryOption } from "../../interfaces/deliveryOption";
import {
  approveClient,
  checkClientHasOrders,
  checkEmailExists,
  checkForms,
  deleteClientBase,
  downloadMailingWindowClient,
  GetAllGeneralClientDocuments,
  getClient,
  updateClient,
  updateClientStatus,
} from "../../services/clientService";
import {
  deleteAdditionalContact,
  getAdditionalContacts,
  updateAdditionalContact,
} from "../../services/additionalContactService";
import { printClientProfile } from "../../services/clientService";
import Spinner from "../../components/component-tools/Spinner";
import { getDeliveryOptions } from "../../services/deliveryOptionsService";
import { getStates } from "../../services/helpers";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AddAdditionalContact from "../../components/client-components/AddAdditionalContact";
import { createReferenceQuestionnaire } from "../../services/referenceQuestionnaireService";
import { createCandidateQuestionnaire } from "../../services/candidateQuestionnaireService";
import { AdditionalForm } from "../../interfaces/additionalForm";
import { AdditionalFormType } from "../../interfaces/additionalFormType";
import { HttpService } from "../../services/httpService";
import {
  deleteAdditionalForm,
  getAdditionalFormsByClientID,
  getAdditionalFormTypes,
} from "../../services/additionalFormService";
import UploadAdditionalForm from "../../components/client-components/UploadAdditionalForm";
import ToolTip from "../../components/component-tools/ToolTip";
import { getDenominations } from "../../services/denominationService";
import { Denomination } from "../../interfaces/denomination";
import DownloadClientForm from "../../components/client-components/DownloadClientForm";
import UploadGeneralClientDoc from "../../components/client-components/UploadGeneralClientDoc";
import DownloadGeneralClientDocs, {
  ClientResponseFile,
} from "../../components/client-components/DownloadGeneralClientDocs";
import ConfirmPopup from "../../components/client-components/ConfirmPopup";
import { ReferenceFeeTier } from "../../interfaces/referenceFeeTier";
import { getReferenceFeeTiers } from "../../services/referenceFeeTierService";
import ConfirmDelete from "../../components/component-tools/ConfirmDelete";
import { createReferenceAuthorization } from "../../services/referenceAuthorizationService";

const ViewClient = () => {
  const { clientID } = useParams();
  const states = getStates();
  const [client, setClient] = useState<Client>({
    clientID: 0,
    abbreviation: "",
    primaryEmail: "",
    clientName: "",
    phoneNumber: "",
    fax: "",
    address: "",
    address2: "",
    city: "",
    state: "",
    zipCode: "",
    primaryTitle: "",
    primaryFirstName: "",
    primaryLastName: "",
    primaryJobTitle: "",
    diocese: "",
    diocese2: "",
    denomination: "Episcopal",
    deliveryOptionID: 0,
    isActive: true,
    clientStatusID: 0,
    createdDate: new Date(),
    lastUpdatedDate: new Date(),
    discountPercent: 0,
    emailFrequencyID: 0,
    questionnairesReviewed: false,
    referenceFeeTier: 1,
    primaryInvoiceManager: true,
  });
  const [additionalContacts, setAdditionalContacts] = useState<
    AdditionalContact[] | null
  >(null);
  const [deliveryOptions, setDeliveryOptions] = useState<DeliveryOption[]>([]);
  const [referenceFeeTiers, setReferenceFeeTiers] = useState<
    ReferenceFeeTier[]
  >([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [showAddContactPopup, setShowAddContactPopup] = useState(false);
  const [showChangeActiveStatus, setShowChangeActiveStatus] = useState(false);
  const [showApproveClient, setShowApproveClient] = useState(false);
  const [additionalForms, setAdditionalForms] = useState<AdditionalForm[]>([]);
  const [additionalFormTypes, setAdditionalFormTypes] = useState<
    AdditionalFormType[]
  >([]);
  const [signedUrls, setSignedUrls] = useState<Record<number, string>>({});
  const [showUploadAdditionalFormPopup, setShowUploadAdditionalFormPopup] =
    useState(false);
  const [showDownloadClientFormPopup, setShowDownloadClientFormPopup] =
    useState(false);
  const [fiveYear, setFiveYear] = useState(false);
  const [showUploadGeneralClientDocPopup, setShowUploadGeneralClientDocPopup] =
    useState(false);
  const [
    showDownloadGeneralClientDocsPopup,
    setShowDownloadGeneralClientDocsPopup,
  ] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const [denominations, setDenominations] = useState<Denomination[]>([]);
  const [showAbbreviation, setShowAbbreviation] = useState(false);
  const [canApprove, setCanApprove] = useState(false);
  const [formsMessage, setFormsMessage] = useState("");
  const [showConfirmationEmail, setShowConfirmationEmail] = useState(false);
  const [confirmHandlers, setConfirmHandlers] = useState<{
    onConfirm: () => void;
    onCancel: () => void;
  } | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [clientDocCount, setClientDocCount] = useState(0);
  const [hasOrders, setHasOrders] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null);

  const containerMap = useMemo<Record<number, string>>(
    () => ({
      1: "signatures",
      2: "credit-report-forms",
      3: "ssn-trace-forms",
      4: "user-agreements",
      5: "page-decorations",
    }),
    [],
  );

  useEffect(() => {
    const loadClient = async () => {
      try {
        if (clientID) {
          const numericClientID = parseInt(clientID, 10);
          const clientResponse = await getClient(numericClientID);
          setClient(clientResponse);

          const [showAbbreviationResponse, formsMessage] = await checkForms(
            clientResponse.clientID,
          );
          setShowAbbreviation(showAbbreviationResponse);
          setFormsMessage(formsMessage);

          const additionalContacts = await getAdditionalContacts(
            clientResponse.clientID,
          );
          setAdditionalContacts(additionalContacts);

          const options = await getDeliveryOptions();
          setDeliveryOptions(options);

          const referenceTiers = await getReferenceFeeTiers();
          setReferenceFeeTiers(referenceTiers);

          const hasOrdersResponse = await checkClientHasOrders(
            clientResponse.clientID,
          );
          setHasOrders(hasOrdersResponse);

          const additionalFormsResponse = await getAdditionalFormsByClientID(
            clientResponse.clientID,
          );
          setAdditionalForms(additionalFormsResponse);

          const additionalFormTypesResponse = await getAdditionalFormTypes();
          setAdditionalFormTypes(additionalFormTypesResponse);

          //get the urls
          const urlPromises = additionalFormsResponse.map(async (form) => {
            const container = containerMap[form.additionalFormTypeID];
            if (container && clientResponse) {
              try {
                const signedUrl = await HttpService.getFileAdmin(
                  container,
                  clientResponse.clientID.toString(),
                  form.completedFormUrl,
                );
                return { id: form.additionalFormID, signedUrl };
              } catch (error) {
                console.error(
                  `Failed to fetch signed URL for form ID ${form.additionalFormID}:`,
                  error,
                );
                return { id: form.additionalFormID, signedUrl: null };
              }
            } else {
              console.warn(
                `No container mapping for form ID ${form.additionalFormID}`,
              );
              return { id: form.additionalFormID, signedUrl: null };
            }
          });

          const urlResults = await Promise.all(urlPromises);
          const urlMap: Record<number, string> = Object.fromEntries(
            urlResults
              .filter(({ signedUrl }) => signedUrl !== null)
              .map(({ id, signedUrl }) => [Number(id), signedUrl as string]),
          );

          setSignedUrls(urlMap);

          const denominationsResponse = await getDenominations();
          const excludedDenominations = [2, 3, 4, 12];
          const filteredDenominations = denominationsResponse.filter(
            (denomination) =>
              !excludedDenominations.includes(denomination.denominationID),
          );
          setDenominations(filteredDenominations);

          //if client is ACNA
          const denomination = filteredDenominations.find(
            (d) => d.denominationName === clientResponse.denomination,
          );
          if (denomination?.denominationID === 1) {
            setFiveYear(true);
          }

          const data: ClientResponseFile[] =
            await GetAllGeneralClientDocuments(numericClientID);
          setClientDocCount(data.length);
        }
      } catch (error) {
        console.error("Error fetching client information:", error);
      } finally {
        setLoading(false);
      }
    };

    loadClient();
  }, [clientID, location.pathname, navigate, containerMap]);

  const updateDocCount = async () => {
    const data: ClientResponseFile[] = await GetAllGeneralClientDocuments(
      client.clientID,
    );
    setClientDocCount(data.length);
  };

  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.successMessage, location.pathname, navigate]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  useEffect(() => {
    const canApproveResult =
      !!client.denomination && !!client.diocese && !!client.phoneNumber;
    setCanApprove(canApproveResult);
  }, [client.denomination, client.diocese, client.phoneNumber]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: string,
  ) => {
    const value =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    setEditing(true);
    setClient((prevClient) => ({
      ...prevClient!,
      [field]: value,
    }));
  };

  const handleAdditionalContactChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    field: string,
    index: number,
  ) => {
    const { type, value } = e.target;
    const checked =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : undefined;

    setAdditionalContacts(
      (prevContacts) =>
        prevContacts?.map((contact, i) =>
          i === index
            ? {
                ...contact,
                [field]: type === "checkbox" ? checked : value,
              }
            : contact,
        ) || [],
    );
  };

  const handleClientSave = async () => {
    try {
      if (!client) return;
      const duplicate = await checkEmailExists(
        client.primaryEmail,
        client.clientID,
      );
      if (duplicate) {
        const confirmed = await confirmEmailOverwrite();

        if (!confirmed) {
          setSuccessMessage("");
          return;
        }
      }

      setLoadingSpinner(true);
      await updateClient(client.clientID, client);
      setSuccessMessage("Client information saved successfully.");
    } catch (error) {
      console.error("Error saving client profile information:", error);
      setSuccessMessage("");
    } finally {
      setEditing(false);
      setLoadingSpinner(false);
    }
  };

  const confirmEmailOverwrite = (): Promise<boolean> => {
    return new Promise((resolve) => {
      const handleConfirm = () => {
        setShowConfirmationEmail(false);
        resolve(true);
      };

      const handleCancel = () => {
        setShowConfirmationEmail(false);
        resolve(false);
        window.location.reload();
      };

      setConfirmHandlers({ onConfirm: handleConfirm, onCancel: handleCancel });
      setShowConfirmationEmail(true);
    });
  };

  const handleDeleteAdditionalContact = async (contactID: number) => {
    try {
      await deleteAdditionalContact(contactID);
      window.location.reload();
      setSuccessMessage("Contact Deleted.");
    } catch (error) {
      console.error("Error deleting contact:", error);
      setSuccessMessage("");
    }
  };

  const handleUpdateAdditionalContact = async (contact: AdditionalContact) => {
    try {
      const duplicate = await checkEmailExists(
        contact.contactEmail,
        client.clientID,
      );
      if (duplicate) {
        const confirmed = await confirmEmailOverwrite();

        if (!confirmed) {
          setSuccessMessage("");
          return;
        }
      }

      await updateAdditionalContact(contact);
      setSuccessMessage("Contact updated.");
    } catch (error) {
      console.error("Error updating contact:", error);
      setSuccessMessage("");
    }
  };

  const handlePrintProfile = async () => {
    try {
      if (!client) {
        setSuccessMessage("Please fix validation errors before printing.");
        return;
      }

      setLoading(true);

      const pdfBlob = await printClientProfile(client.clientID, true);

      if (pdfBlob) {
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "ClientProfile.pdf");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setLoading(false);
      } else {
        setSuccessMessage("Failed to generate PDF.");
      }
    } catch (error) {
      setLoading(false);
      console.error("Error generating profile PDF:", error);
      setSuccessMessage("An error occurred while generating the PDF.");
    }
  };

  const handleClientStatusChange = async () => {
    try {
      if (client) {
        setLoading(true);
        if (client.isActive) {
          //inactive
          await updateClientStatus(client.clientID, 2);
          client.isActive = false;
        } else {
          //active
          await updateClientStatus(client.clientID, 1);
          client.isActive = true;
        }
        setSuccessMessage("Client status updated successfully.");
      } else {
        setErrorMessage("Invalid client ID or client not available.");
      }
    } catch (error) {
      console.error("Error updating client status:", error);
    } finally {
      setLoading(false);
      setShowChangeActiveStatus(false);
    }
  };

  const handleApproveClient = async () => {
    try {
      setLoadingSpinner(true);

      var clientNew = await getClient(client.clientID);
      if (
        clientNew.diocese?.trim() === "" ||
        clientNew.denomination?.trim() === ""
      ) {
        setErrorMessage(
          "You need to save client name merge field, denomination and phone number first.",
        );
        return;
      }

      const approveResponse = await approveClient(clientNew.clientID);
      if (approveResponse?.warning) {
        setSuccessMessage("Client approved, but email failed to send.");
      } else {
        if (fiveYear) {
          //create 5 year ref questionnaire
          await createCandidateQuestionnaire(
            clientNew.clientID,
            true,
            clientNew.diocese,
          );
        }
        await createReferenceQuestionnaire(clientNew.clientID);
        await createCandidateQuestionnaire(
          clientNew.clientID,
          false,
          clientNew.diocese,
        );
        await createReferenceAuthorization(clientNew.clientID);
        setSuccessMessage("Client approved successfully.");
      }
      client.clientStatusID = 2;
      setShowApproveClient(false);
    } catch (error) {
      console.error("Error approving client:", error);
      setErrorMessage("Error approving client.");
    } finally {
      setLoadingSpinner(false);
      setShowChangeActiveStatus(false);
    }
  };

  const handleDownloadMailingWindow = async () => {
    try {
      setLoadingSpinner(true);

      const response = await downloadMailingWindowClient(client);

      if (!response) throw new Error("No response from server");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const fileName = "Mailing_Window.pdf";
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return true;
    } catch (error) {
      console.error("Error handling form download:", error);
      setErrorMessage("Error downloading mailing window.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleDeleteForm = async (formTypeID: number) => {
    try {
      setLoadingSpinner(true);
      if (!client) return;
      const success = await deleteAdditionalForm(client.clientID, formTypeID);

      if (success) {
        setSuccessMessage("Form deleted successfully.");
        window.location.reload();
      }
    } catch (error) {
      console.error("Error deleting form:", error);
      setErrorMessage("An error occurred while deleting the form.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleClientDelete = async () => {
    try {
      setShowDeleteConfirmation(false);
      setLoadingSpinner(true);

      await deleteClientBase(client.clientID);
      navigate("/admin/clients", {
        state: { successMessage: "Client successfully deleted." },
      });
    } catch (error) {
      console.error("Error deleting client:", error);
    } finally {
      setLoadingSpinner(false);
    }
  };

  const renderFormButtons = () => {
    if (additionalForms.length === 0) {
      return <div>*Migrated client, no additional forms*</div>;
    }
    return additionalForms.map((additionalForm) => {
      const additionalFormType = additionalFormTypes.find(
        (type) =>
          type.additionalFormTypeID === additionalForm.additionalFormTypeID,
      );

      const signedUrl = signedUrls[additionalForm.additionalFormID];

      if (additionalFormType && signedUrl) {
        return (
          <>
            <div
              style={{ display: "flex", height: "max-content" }}
              key={additionalForm.additionalFormID}
            >
              <button
                className="smbtn"
                onClick={() => window.open(signedUrl, "_blank")}
              >
                {additionalFormType.description || "No Description"}
                <i className="btn-icon bi bi-box-arrow-up-right"></i>
              </button>
              <div
                onClick={() =>
                  setDeleteTarget(additionalForm.additionalFormTypeID)
                }
              >
                <i
                  className="bi bi-x"
                  style={{ color: "red", cursor: "pointer" }}
                ></i>
              </div>
            </div>
            <div></div>
          </>
        );
      }

      return (
        <button
          key={additionalForm.additionalFormID}
          className="smbtn"
          disabled
        >
          {additionalFormType?.description}
        </button>
      );
    });
  };

  return (
    <div className="content-container">
      <div className="header-container">
        <div className="page-items-container" style={{ alignItems: "center" }}>
          {" "}
          <h1>View Client</h1>
          {client.clientStatusID === 1 ? (
            <div
              className="chip-date"
              style={{
                gap: "10px",
                justifyContent: "end",
                cursor: "pointer",
              }}
              onClick={() => {
                setShowApproveClient(!showApproveClient);
              }}
            >
              <div>{"Pending"}</div>
            </div>
          ) : (
            <div
              className="chip-date"
              style={{
                gap: "10px",
                justifyContent: "end",
                cursor: "pointer",
              }}
              onClick={() => {
                setShowChangeActiveStatus(!showChangeActiveStatus);
              }}
            >
              <div>{client.isActive ? "Active" : "Inactive"}</div>
            </div>
          )}
          {client.clientStatusID === 3 && (
            <div className="green-label">
              <div>{"New Client"}</div>
            </div>
          )}
        </div>

        <div className="page-items-container">
          <div className="field-long">
            <label className="field-label">Client Name:</label>
            <div>
              <input
                className="form-control"
                type="text"
                value={client?.clientName || ""}
                disabled
                readOnly
              />
            </div>
          </div>
          <div className="field-long">
            <label className="field-label">Abbreviation:</label>
            <div>
              <input
                className="form-control"
                type="text"
                value={client?.abbreviation}
                disabled
                readOnly
              />
            </div>
          </div>
        </div>
      </div>

      {/* Change Client Status Popup */}
      {showChangeActiveStatus && (
        <>
          <div
            className="background-mask"
            onClick={() => setShowChangeActiveStatus(false)}
          ></div>
          <div className="filter-popup">
            <div className="close-button-container">
              <button
                className="close-button"
                onClick={() => setShowChangeActiveStatus(false)}
              >
                <i className="bi bi-x-circle"></i>
              </button>
            </div>
            <div className="filter-header">
              <h2>Change Client Status</h2>
              <div>
                <div>{`Change status to ${
                  client.isActive ? "Inactive?" : "Active?"
                }`}</div>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: "20px",
                justifyContent: "center",
                marginTop: "20px",
              }}
            >
              <div
                className="chip-date"
                style={{ cursor: "pointer" }}
                onClick={() => handleClientStatusChange()}
              >
                <div>{`${client.isActive ? "Inactivate" : "Activate"}`}</div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Approve Client Popup */}
      {showApproveClient && (
        <>
          <div
            className="background-mask"
            onClick={() => setShowApproveClient(false)}
          ></div>
          <div className="filter-popup">
            <div className="close-button-container">
              <button
                className="close-button"
                onClick={() => setShowApproveClient(false)}
              >
                <i className="bi bi-x-circle"></i>
              </button>
            </div>
            <div className="filter-header">
              <h2>Approve Client</h2>
              <div>
                Approve this Client and allow them to view their portal and
                start initiating orders. This will send an email notice to them
                and{" "}
                <b>create default candidate and reference questionnaires.</b>{" "}
                Questionnaires will NOT be visible immediately to client.
              </div>
              {!canApprove && (
                <div className="error">
                  Add Denomination, Client Name Merge field and Phone Number AND
                  SAVE CLIENT before approving!
                </div>
              )}
            </div>
            <div
              style={{
                display: "flex",
                gap: "20px",
                justifyContent: "center",
                marginTop: "20px",
              }}
            >
              <button
                className={`oxbtn btn-green ${!canApprove ? "disabled" : ""}`}
                onClick={() => handleApproveClient()}
                disabled={!canApprove}
              >
                Approve
              </button>
            </div>
          </div>
        </>
      )}
      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          {!showAbbreviation && (
            <div className="text-container error">{formsMessage}</div>
          )}
          <div className="divider"></div>
          <div className="data-container">
            <div
              style={{
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <div className="text-header">
                Client Information
                <button
                  onClick={() => handlePrintProfile()}
                  className="oxbtn btn-light"
                  style={{ marginLeft: "10px" }}
                >
                  Print Profile <i className="bi bi-printer-fill btn-icon"></i>
                </button>
                {loading && <Spinner />}
              </div>
              <button
                className="smbtn btn-grey"
                onClick={() =>
                  navigate(`/admin/view-client/history/${client.clientID}`)
                }
              >
                View Client History
                <i className="btn-icon bi bi-arrow-right-circle"></i>
              </button>
            </div>

            <div className="field-stacked" style={{ flex: "0 0 35%" }}>
              <label className="field-label">Organization Name:</label>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  value={client?.clientName || ""}
                  onChange={(e) => handleInputChange(e, "clientName")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <div style={{ display: "flex", gap: "5px" }}>
                <label className="field-label">Denomination:</label>
                <ToolTip
                  tooltipText="Selection of Questionnaires and ID instructions used"
                  placement="right"
                  className="info-icon-spec"
                ></ToolTip>
              </div>
              <div className="field-input">
                <select
                  className="form-control"
                  value={client?.denomination || ""}
                  onChange={(e) => handleInputChange(e, "denomination")}
                  disabled={client.clientStatusID !== 1}
                >
                  <option value="">Select a Denomination</option>
                  {denominations.map((denomination) => (
                    <option
                      key={denomination.denominationID}
                      value={denomination.denominationName}
                    >
                      {denomination.denominationName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 100%" }}></div>
            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Client Name Merge Field:</label>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  value={client?.diocese || ""}
                  onChange={(e) => handleInputChange(e, "diocese")}
                  maxLength={500}
                />
                <div
                  style={{
                    fontSize: "14px",
                    marginTop: "4px",
                    textAlign: "right",
                  }}
                >
                  {client?.diocese?.length ?? 0}/500
                </div>
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <div style={{ display: "flex", gap: "5px" }}>
                <label className="field-label">
                  Client Name Merge Field 2:
                </label>
                <ToolTip
                  tooltipText="Field shows above Client Name Merge Field."
                  placement="right"
                  className="info-icon-spec"
                ></ToolTip>
              </div>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  value={client?.diocese2 || ""}
                  onChange={(e) => handleInputChange(e, "diocese2")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 40%" }}></div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Phone Number:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={client?.phoneNumber || ""}
                  onChange={(e) => handleInputChange(e, "phoneNumber")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Confidential Fax:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={client?.fax || ""}
                  onChange={(e) => handleInputChange(e, "fax")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 100%" }}></div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Address:</label>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  value={client?.address || ""}
                  onChange={(e) => handleInputChange(e, "address")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 15%" }}>
              <label className="field-label">Address 2:</label>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  value={client?.address2 || ""}
                  onChange={(e) => handleInputChange(e, "address2")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 15%" }}>
              <label className="field-label">City:</label>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  value={client?.city || ""}
                  onChange={(e) => handleInputChange(e, "city")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 15%" }}>
              <label className="field-label">State:</label>
              <div className="field-input">
                <select
                  className={`form-select`}
                  value={client?.state || ""}
                  onChange={(e) => handleInputChange(e, "state")}
                >
                  <option value="">Select State</option>
                  {states.map((state) => (
                    <option key={state.abbreviation} value={state.abbreviation}>
                      {state.name} ({state.abbreviation})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 15%" }}>
              <label className="field-label">Zip Code:</label>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  value={client?.zipCode || ""}
                  onChange={(e) => handleInputChange(e, "zipCode")}
                />
              </div>
            </div>
            {client?.state === "INT" && (
              <div className="field-stacked" style={{ flex: "0 0 15%" }}>
                <label className="field-label">Country:</label>
                <div className="field-input">
                  <input
                    className="form-control"
                    type="text"
                    value={client.country ? client.country : ""}
                    onChange={(e) => handleInputChange(e, "country")}
                  />
                </div>
              </div>
            )}
            <div className="field-stacked" style={{ flex: "0 0 100%" }}></div>
            <div className="field-stacked" style={{ flex: "0 0 15%" }}>
              <label className="field-label">Final Report Delivery:</label>
              <div
                style={{
                  flex: "0 0 30%",
                  alignSelf: "end",
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                <select
                  className={`form-select`}
                  value={client?.deliveryOptionID || ""}
                  onChange={(e) => handleInputChange(e, "deliveryOptionID")}
                >
                  {deliveryOptions.map((option) => (
                    <option
                      key={option.deliveryOptionID}
                      value={option.deliveryOptionID}
                    >
                      {option.description}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 10%" }}>
              <label className="field-label">Discount:</label>
              <div className="input-group">
                <input
                  className="form-control"
                  type="number"
                  min={0}
                  max={100}
                  value={client?.discountPercent ?? ""}
                  onChange={(e) => handleInputChange(e, "discountPercent")}
                />
                <span className="input-group-text">%</span>
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 10%" }}>
              <div style={{ display: "flex", gap: "5px" }}>
                <label className="field-label">Require Notary:</label>
                <ToolTip
                  tooltipText="Add a notary section to candidate questionnaires."
                  placement="right"
                  className="info-icon-spec"
                ></ToolTip>
              </div>
              <div>
                <input
                  className={`form-check-input`}
                  type="checkbox"
                  checked={client?.requireNotary || false}
                  onChange={(e) => handleInputChange(e, "requireNotary")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 15%" }}>
              <label className="field-label">Reference Fee Tier:</label>
              <div
                style={{
                  flex: "0 0 30%",
                  alignSelf: "end",
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                <select
                  className={`form-select`}
                  value={client?.referenceFeeTier || ""}
                  onChange={(e) => handleInputChange(e, "referenceFeeTier")}
                >
                  {referenceFeeTiers.map((option) => (
                    <option
                      key={option.referenceFeeTierID}
                      value={option.referenceFeeTierID}
                    >
                      {option.description}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 100%" }}>
              <label>Comments:</label>
              <div>
                <textarea
                  className="form-control"
                  value={client.comments}
                  onChange={(e) => handleInputChange(e, "comments")}
                  style={{ height: "100px" }}
                  maxLength={4000}
                />
                <div
                  style={{
                    fontSize: "14px",
                    marginTop: "4px",
                    textAlign: "right",
                  }}
                >
                  {client.comments?.length ?? 0}/4000
                </div>
              </div>
            </div>

            <div
              className="text-sub-header"
              style={{
                flex: "0 0 100%",
                display: "flex",
                flexDirection: "row",
                gap: "5px",
              }}
            >
              <div>Primary Contact Information</div>
              <ToolTip
                tooltipText="All client communications should be addressed to this person."
                placement="right"
                className="info-icon-spec"
              ></ToolTip>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Title:</label>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  placeholder="Mr./Ms., The Rev., ..."
                  value={client?.primaryTitle || ""}
                  onChange={(e) => handleInputChange(e, "primaryTitle")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">First Name:</label>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  value={client?.primaryFirstName || ""}
                  onChange={(e) => handleInputChange(e, "primaryFirstName")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 25%" }}>
              <label className="field-label">Last Name:</label>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  value={client?.primaryLastName || ""}
                  onChange={(e) => handleInputChange(e, "primaryLastName")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 25%" }}></div>
            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Job Title:</label>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  value={client?.primaryJobTitle || ""}
                  onChange={(e) => handleInputChange(e, "primaryJobTitle")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Email:</label>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  value={client?.primaryEmail || ""}
                  onChange={(e) => handleInputChange(e, "primaryEmail")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 10%" }}>
              <label className="field-label">Invoice Manager:</label>
              <div>
                <input
                  className={`form-check-input`}
                  type="checkbox"
                  checked={client.primaryInvoiceManager || false}
                  onChange={(e) =>
                    handleInputChange(e, "primaryInvoiceManager")
                  }
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 10%" }}>
              <label className="field-label">
                Receive Email Notifications:
              </label>
              <div>
                <input
                  className={`form-check-input`}
                  type="checkbox"
                  checked={client?.emailNotifications || false}
                  onChange={(e) => handleInputChange(e, "emailNotifications")}
                />
              </div>
            </div>
            <div>
              *Use save button at the bottom to save primary contact information
            </div>
            <div style={{ flex: "0 0 100%" }}></div>

            <div className="text-sub-header" style={{ flex: "0 0 100%" }}>
              Additional Contacts
            </div>
            {additionalContacts?.length !== 0 ? (
              <div>
                {additionalContacts?.map((contact, index) => (
                  <div
                    key={contact.additionalContactID}
                    className="additional-contact-group"
                  >
                    <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                      <label className="field-label">Title:</label>
                      <div className="field-input">
                        <input
                          className="form-control"
                          type="text"
                          placeholder="Mr./Ms., The Rev., ..."
                          value={contact.contactTitle || ""}
                          onChange={(e) =>
                            handleAdditionalContactChange(
                              e,
                              "contactTitle",
                              index,
                            )
                          }
                        />
                      </div>
                    </div>
                    <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                      <label className="field-label">First Name:</label>
                      <div className="field-input">
                        <input
                          className={`form-control`}
                          type="text"
                          value={contact.contactFirstName || ""}
                          onChange={(e) =>
                            handleAdditionalContactChange(
                              e,
                              "contactFirstName",
                              index,
                            )
                          }
                        />
                      </div>
                    </div>
                    <div className="field-stacked" style={{ flex: "0 0 25%" }}>
                      <label className="field-label">Last Name:</label>
                      <div className="field-input">
                        <input
                          className={`form-control`}
                          type="text"
                          value={contact.contactLastName || ""}
                          onChange={(e) =>
                            handleAdditionalContactChange(
                              e,
                              "contactLastName",
                              index,
                            )
                          }
                        />
                      </div>
                    </div>
                    <div
                      className="field-stacked"
                      style={{ flex: "0 0 25%" }}
                    ></div>
                    <div className="field-stacked" style={{ flex: "0 0 30%" }}>
                      <label className="field-label">Job Title:</label>
                      <div className="field-input">
                        <input
                          className="form-control"
                          type="text"
                          value={contact.contactJobTitle || ""}
                          onChange={(e) =>
                            handleAdditionalContactChange(
                              e,
                              "contactJobTitle",
                              index,
                            )
                          }
                        />
                      </div>
                    </div>
                    <div className="field-stacked" style={{ flex: "0 0 30%" }}>
                      <label className="field-label">Email:</label>
                      <div className="field-input">
                        <input
                          className={`form-control`}
                          type="text"
                          value={contact.contactEmail || ""}
                          onChange={(e) =>
                            handleAdditionalContactChange(
                              e,
                              "contactEmail",
                              index,
                            )
                          }
                        />
                      </div>
                    </div>
                    <div className="field-stacked" style={{ flex: "0 0 10%" }}>
                      <label className="field-label">Invoice Manager:</label>
                      <div>
                        <input
                          className={`form-check-input`}
                          type="checkbox"
                          checked={contact.invoiceManager || false}
                          onChange={(e) =>
                            handleAdditionalContactChange(
                              e,
                              "invoiceManager",
                              index,
                            )
                          }
                        />
                      </div>
                    </div>
                    <div className="field-stacked" style={{ flex: "0 0 10%" }}>
                      <label className="field-label">
                        Receive Email Notifications:
                      </label>
                      <div>
                        <input
                          className={`form-check-input`}
                          type="checkbox"
                          checked={contact.emailNotifications || false}
                          onChange={(e) =>
                            handleAdditionalContactChange(
                              e,
                              "emailNotifications",
                              index,
                            )
                          }
                        />
                      </div>
                    </div>
                    <div style={{ flex: "0 0 10%" }}></div>
                    <div
                      className="action-buttons"
                      style={{ justifyContent: "left" }}
                    >
                      <div>
                        <button
                          className="smbtn btn-green"
                          onClick={() => handleUpdateAdditionalContact(contact)}
                        >
                          Save
                        </button>
                      </div>
                      <div>
                        <button
                          className="smbtn btn-red"
                          onClick={() =>
                            handleDeleteAdditionalContact(
                              contact.additionalContactID,
                            )
                          }
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <div>
                      *Only saves or deletes additional contact information
                    </div>
                    {/* {showConfirmation && (
                      <ConfirmDelete
                        onConfirm={() =>
                          handleDeleteAdditionalContact(
                            contact.additionalContactID
                          )
                        }
                        onCancel={() => setShowConfirmation(false)}
                        message="Are you sure you want to delete this additional contact?"
                      />
                    )} */}
                  </div>
                ))}
              </div>
            ) : (
              <div>No Additional Contacts.</div>
            )}

            {/* implement popup here */}
            <div className="btn-center" style={{ flex: "0 0 100%" }}>
              <button
                className="oxbtn btn-purple"
                onClick={() => setShowAddContactPopup(true)}
              >
                Add Additional Contact
                <i className="bi bi-plus-lg btn-icon"></i>
              </button>
            </div>
            {showAddContactPopup && (
              <AddAdditionalContact
                client={client}
                onClose={() => {
                  setShowAddContactPopup(false);
                  window.location.reload();
                }}
              />
            )}
          </div>

          {showConfirmationEmail && confirmHandlers && (
            <ConfirmPopup
              onConfirm={confirmHandlers.onConfirm}
              onCancel={confirmHandlers.onCancel}
              message="There is another client containing this email. Are you sure you want to link them?"
              btnColor="green"
            />
          )}

          {/* Questionnaires */}
          {client.clientStatusID !== 1 && (
            <div className="data-container">
              <div
                style={{
                  flex: "0 0 100%",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <div className="text-header">Client Forms</div>
                <div className="field-long">
                  <label className="field-label">Admin Reviewed:</label>
                  <div>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={
                        client.questionnairesReviewed
                          ? client.questionnairesReviewed
                          : false
                      }
                      onChange={(e) =>
                        handleInputChange(e, "questionnairesReviewed")
                      }
                    />
                  </div>
                  <ToolTip
                    tooltipText="Questionnaires are visible to client in their portal."
                    placement="top"
                    className="info-icon-spec"
                  ></ToolTip>
                </div>
              </div>
              <div className="questionnaire-list-small">
                <div
                  className="questionnaire-card-small"
                  style={{ cursor: "default" }}
                >
                  <div className="questionnaire-card-small-info">
                    <div className="questionnaire-name">
                      Reference Authorization
                    </div>
                    <div>
                      <button
                        className="smbtn btn-grey"
                        onClick={() =>
                          navigate(
                            `/admin/view-client/reference-authorization/${client.clientID}`,
                          )
                        }
                      >
                        View/Edit Reference Authorization{" "}
                        <i className="btn-icon bi bi-arrow-right-circle"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div
                  className="questionnaire-card-small"
                  style={{ cursor: "default" }}
                >
                  <div className="questionnaire-card-small-info">
                    <div className="questionnaire-name">
                      Reference Questionnaire
                    </div>
                    <div>
                      <button
                        className="smbtn btn-grey"
                        onClick={() =>
                          navigate(
                            `/admin/view-client/questionnaire/${client.clientID}`,
                            {
                              state: {
                                questionnaireType: "Reference",
                              },
                            },
                          )
                        }
                      >
                        View/Edit Questionnaire{" "}
                        <i className="btn-icon bi bi-arrow-right-circle"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div
                  className="questionnaire-card-small"
                  style={{ cursor: "default" }}
                >
                  <div className="questionnaire-card-small-info">
                    <div className="questionnaire-name">
                      {fiveYear
                        ? "10/15 Year Candidate Questionnaire"
                        : "Candidate Questionnaire"}
                    </div>
                    <div>
                      <button
                        className="smbtn btn-grey"
                        onClick={() =>
                          navigate(
                            `/admin/view-client/questionnaire/${client.clientID}`,
                            {
                              state: {
                                questionnaireType: "Candidate",
                                fiveYear: false,
                              },
                            },
                          )
                        }
                      >
                        View/Edit Questionnaire{" "}
                        <i className="btn-icon bi bi-arrow-right-circle"></i>
                      </button>
                    </div>
                  </div>
                </div>
                {fiveYear && (
                  <div
                    className="questionnaire-card-small"
                    style={{ cursor: "default" }}
                  >
                    <div className="questionnaire-card-small-info">
                      <div className="questionnaire-name">
                        5 Year Candidate Questionnaire
                      </div>
                      <div>
                        <button
                          className="smbtn btn-grey"
                          onClick={() =>
                            navigate(
                              `/admin/view-client/questionnaire/${client.clientID}`,
                              {
                                state: {
                                  questionnaireType: "Candidate",
                                  fiveYear: true,
                                },
                              },
                            )
                          }
                        >
                          View/Edit Questionnaire{" "}
                          <i className="btn-icon bi bi-arrow-right-circle"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Position Codes */}
          <div
            className="data-container"
            style={{ justifyContent: "space-between" }}
          >
            <div className="text-header">Position Codes</div>
            <button
              className="smbtn btn-grey"
              onClick={() =>
                navigate(`/admin/view-client/position-codes/${client.clientID}`)
              }
            >
              View/Edit Position Codes
              <i className="btn-icon bi bi-arrow-right-circle"></i>
            </button>
          </div>

          {/* Additional Forms */}
          <div className="data-container">
            <div className="text-header" style={{ flex: "0 0 100%" }}>
              Additional Forms{" "}
              <button
                style={{ marginLeft: "20px" }}
                className="smbtn btn-grey"
                onClick={() => setShowUploadAdditionalFormPopup(true)}
              >
                Upload Additional Form
              </button>
              <button
                style={{ marginLeft: "20px" }}
                className={`smbtn btn-grey`}
                onClick={() => handleDownloadMailingWindow()}
              >
                Download Mailing Window
              </button>
              <button
                style={{ marginLeft: "20px" }}
                className={`smbtn btn-grey`}
                onClick={() => setShowDownloadClientFormPopup(true)}
              >
                Download Client Form
              </button>
            </div>

            {showUploadAdditionalFormPopup && (
              <UploadAdditionalForm
                client={client}
                onClose={() => {
                  setShowUploadAdditionalFormPopup(false);
                  window.location.reload();
                }}
              />
            )}
            {showDownloadClientFormPopup && client && (
              <DownloadClientForm
                client={client}
                onClose={() => {
                  setShowDownloadClientFormPopup(false);
                }}
              />
            )}
            {renderFormButtons()}
            {deleteTarget && (
              <ConfirmDelete
                onConfirm={() => {
                  handleDeleteForm(deleteTarget);
                  setDeleteTarget(null);
                }}
                onCancel={() => setDeleteTarget(null)}
              />
            )}
          </div>

          {/* Document Management */}
          <div className="data-container">
            <div
              className="text-header"
              style={{ display: "flex", flex: "0 0 100%" }}
            >
              Document Management{" "}
              <div style={{ display: "flex", gap: "5px" }}>
                <div>
                  <button
                    style={{ marginLeft: "20px" }}
                    className={`smbtn btn-grey`}
                    onClick={() => setShowUploadGeneralClientDocPopup(true)}
                  >
                    General Document Upload
                  </button>
                </div>
                <ToolTip
                  tooltipText="Use a unique file name to prevent overwriting."
                  placement="right"
                  className="info-icon-spec"
                ></ToolTip>
              </div>
              <div>
                <button
                  style={{ marginLeft: "20px" }}
                  className={`smbtn btn-grey`}
                  onClick={() => setShowDownloadGeneralClientDocsPopup(true)}
                >
                  Download Document
                </button>
              </div>
              {clientDocCount > 0 && (
                <div style={{ display: "flex", alignSelf: "start" }}>
                  <span className="doc-count-dot">{clientDocCount}</span>
                </div>
              )}
            </div>

            {showUploadGeneralClientDocPopup && (
              <UploadGeneralClientDoc
                client={client}
                onClose={() => {
                  setShowUploadGeneralClientDocPopup(false);
                  updateDocCount();
                }}
              />
            )}
            {showDownloadGeneralClientDocsPopup && client && (
              <DownloadGeneralClientDocs
                client={client}
                onClose={() => {
                  setShowDownloadGeneralClientDocsPopup(false);
                  updateDocCount();
                }}
              />
            )}
          </div>
          <div className="action-buttons" style={{ flex: "0 0 100%" }}>
            <button
              className={`oxbtn btn-green ${!editing ? "disabled" : ""} `}
              onClick={() => handleClientSave()}
              disabled={!editing}
            >
              Save
            </button>
            {!hasOrders && (
              <button
                className={`oxbtn btn-red`}
                onClick={() => setShowDeleteConfirmation(true)}
              >
                Delete
              </button>
            )}
            {showDeleteConfirmation && (
              <ConfirmPopup
                onConfirm={() => handleClientDelete()}
                onCancel={() => setShowDeleteConfirmation(false)}
                message="Are you sure you would like to delete this client? There is no un-doing this action."
              />
            )}
          </div>

          {successMessage && (
            <div className="alert-message alert alert-success alert-dismissible fade show">
              {successMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccessMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}
          {errorMessage && (
            <div
              className="alert-message alert alert-danger alert-dismissible fade show"
              role="alert"
            >
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}
        </>
      )}
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default ViewClient;
